import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Mic, Square, Loader2, Volume2, ArrowLeftRight, Crown, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { myiaTexts } from '../../utils/myiaTranslations';
import { usePremiumEnabled } from '../../hooks/usePremiumEnabled';
import { ReportAIContentButton } from '../ReportAIContent';

/**
 * RealTimeTranslator — Two-user conversation on the SAME phone.
 * Top pane = the person facing you (text upside-down so they can read it),
 * bottom pane = you.
 * Each pane has its own mic button. When either speaks, we send audio to the
 * backend which:
 *   1. Transcribes with Whisper
 *   2. Translates with OpenAI
 *   3. Synthesizes TTS in the target language via ElevenLabs
 * Then we play the audio and display the translation in the OTHER pane.
 */
const LANGS = [
  ['fr', '🇫🇷 Français'], ['en', '🇬🇧 English'], ['es', '🇪🇸 Español'],
  ['de', '🇩🇪 Deutsch'], ['it', '🇮🇹 Italiano'], ['pt', '🇵🇹 Português'],
  ['nl', '🇳🇱 Nederlands'], ['ar', '🇸🇦 العربية'], ['ru', '🇷🇺 Русский'],
  ['zh', '🇨🇳 中文'], ['ja', '🇯🇵 日本語'], ['hi', '🇮🇳 हिन्दी'], ['sw', '🇰🇪 Kiswahili'],
];

// v60.1 — estimation du genre de la voix (fréquence fondamentale par autocorrélation) → voix TTS assortie
async function detectVoiceGender(blob) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return 'auto';
    const ctx = new Ctx();
    const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
    const data = buf.getChannelData(0);
    const sr = buf.sampleRate;
    const win = Math.min(data.length, Math.floor(sr * 1.5));
    const start = Math.max(0, Math.floor((data.length - win) / 2));
    const seg = data.subarray(start, start + win);
    let rms = 0; for (let i = 0; i < seg.length; i++) rms += seg[i] * seg[i]; rms = Math.sqrt(rms / seg.length);
    if (rms < 0.01) { ctx.close(); return 'auto'; }
    const minLag = Math.floor(sr / 400), maxLag = Math.floor(sr / 70);
    let best = -1, bestLag = 0;
    for (let lag = minLag; lag <= maxLag; lag++) {
      let sum = 0;
      for (let i = 0; i + lag < seg.length; i += 2) sum += seg[i] * seg[i + lag];
      if (sum > best) { best = sum; bestLag = lag; }
    }
    ctx.close();
    const f0 = bestLag ? sr / bestLag : 0;
    if (!f0) return 'auto';
    return f0 < 165 ? 'male' : 'female';
  } catch { return 'auto'; }
}

export default function RealTimeTranslator() {
  const [regional, setRegional] = useState({ guyane: [], africa: [] });
  useEffect(() => {
    axios.get('/api/gpt-africa/public-preview', { params: { lang: 'fr' } })
      .then((r) => setRegional({ guyane: r.data?.guyane || [], africa: r.data?.africa || [] }))
      .catch(() => {});
  }, []);
  const renderLangOptions = () => (
    <>
      {LANGS.map(([id, lbl]) => <option key={id} value={id} className="bg-gray-900">{lbl}</option>)}
      <optgroup label="GPT Guyane — langues régionales" data-testid="rt-optgroup-guyane">
        {regional.guyane.map((l) => <option key={`g-${l.id}`} value={l.id} className="bg-gray-900">🇬🇫 {l.label}</option>)}
      </optgroup>
      <optgroup label="GPT Africa — langues régionales & traditionnelles" data-testid="rt-optgroup-africa">
        {regional.africa.map((l) => <option key={`a-${l.id}`} value={l.id} className="bg-gray-900">{l.flag || '🌍'} {l.label} ({l.country_name || l.country})</option>)}
      </optgroup>
    </>
  );
  const { language } = useLanguage();
  const M = myiaTexts(language);
  const premiumOn = usePremiumEnabled();
  const [open, setOpen] = useState(false);
  const [langA, setLangA] = useState('fr');  // bottom (you)
  const [langB, setLangB] = useState('en');  // top (other)
  const [textA, setTextA] = useState('');    // what YOU said (in your lang)
  const [textB, setTextB] = useState('');    // what OTHER said (in their lang)
  const [transA, setTransA] = useState('');  // translation shown to OTHER (in B lang)
  const [transB, setTransB] = useState('');  // translation shown to YOU (in A lang)
  const [busy, setBusy] = useState(null);    // 'a' | 'b' | null (transcribing/translating)
  const [rec, setRec] = useState(null);      // 'a' | 'b' | null

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async (side) => {
    if (rec) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        await sendTurn(side, blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRec(side);
    } catch { toast.error(M.rtKo); }
  };

  const stopRec = () => {
    if (mediaRef.current && rec) {
      mediaRef.current.stop();
      setRec(null);
    }
  };

  const sendTurn = async (side, blob) => {
    setBusy(side);
    try {
      const src = side === 'a' ? langA : langB;
      const tgt = side === 'a' ? langB : langA;
      const fd = new FormData();
      fd.append('audio', blob, 'turn.webm');
      fd.append('source_lang', src);
      fd.append('target_lang', tgt);
      fd.append('speak', 'true');
      fd.append('voice_gender', await detectVoiceGender(blob));
      const r = await axios.post('/api/myia-voice/translate/turn', fd);
      const { source_text, target_text, audio_b64 } = r.data || {};
      if (side === 'a') {
        setTextA(source_text || '');
        setTransA(target_text || ''); // shown to B
      } else {
        setTextB(source_text || '');
        setTransB(target_text || ''); // shown to A
      }
      if (audio_b64) {
        const audio = new Audio(`data:audio/mp3;base64,${audio_b64}`);
        audio.play().catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || M.rtKo);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 rounded-2xl bg-gradient-to-br from-orange-500/10 to-pink-500/10 border border-orange-500/30" data-testid="rt-translator-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="text-2xl shrink-0">🗣️</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-orange-200 text-sm">{M.rtTitle}</h4>
            {premiumOn && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-[9px] font-bold text-yellow-300 uppercase tracking-wide" data-testid="rt-translator-premium-badge">
                <Crown className="w-2.5 h-2.5" /> Premium
              </span>
            )}
          </div>
          <p className="text-[11px] text-orange-200/70 mt-0.5 leading-snug">
            {M.rtSub}
          </p>
        </div>
      </div>

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-full py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 font-semibold text-xs flex items-center justify-center gap-2"
          data-testid="rt-translator-open"
        >
          <ArrowLeftRight className="w-4 h-4" /> {M.rtStart}
        </button>
      ) : (
        <div className="fixed inset-0 z-[9000] bg-black flex flex-col" data-testid="rt-translator-fullscreen" data-keep-dark="">
          <button
            onClick={() => { stopRec(); setOpen(false); setTextA(''); setTextB(''); setTransA(''); setTransB(''); }}
            className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}
            aria-label={M.rtClose}
            data-testid="rt-translator-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex-1 flex flex-col overflow-hidden">
          {/* TOP pane — for the OTHER person, text flipped */}
          <div className="p-4 border-b border-orange-500/20 flex-1 flex flex-col justify-center" style={{ transform: 'rotate(180deg)' }}>
            <div className="flex items-center gap-2 mb-2" style={{ direction: 'ltr' }}>
              <select value={langB} onChange={e => setLangB(e.target.value)} className="bg-black/50 border border-orange-500/40 rounded-full px-2 py-1 text-xs text-white font-medium max-w-[60vw]" style={{ color: "#fff", backgroundColor: "rgba(0,0,0,0.6)" }} data-testid="rt-lang-b">
                {renderLangOptions()}
              </select>
              <button
                onClick={() => rec === 'b' ? stopRec() : startRec('b')}
                disabled={busy || (rec && rec !== 'b')}
                className={`p-2 rounded-full flex-shrink-0 ${rec === 'b' ? 'bg-red-500 animate-pulse' : 'bg-gradient-to-r from-orange-500 to-pink-500'} disabled:opacity-40`}
                data-testid="rt-mic-b"
              >
                {rec === 'b' ? <Square className="w-3.5 h-3.5" fill="white" /> : <Mic className="w-3.5 h-3.5" />}
              </button>
              {busy === 'b' && <Loader2 className="w-4 h-4 animate-spin text-orange-300" />}
            </div>
            {(textB || transA) && (
              <div className="space-y-1.5">
                {textB && <p className="text-[13px] font-semibold text-orange-100">{textB}</p>}
                <div className="flex justify-end mt-1"><ReportAIContentButton feature="translator" contentText={`${textB || ''} ${transA || ''}`} testId="ai-report-translator" /></div>
                {transA && (
                  <p className="text-xs text-orange-200/70 italic flex items-start gap-1">
                    <Volume2 className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{transA}</span>
                  </p>
                )}
              </div>
            )}
            {!textB && !transA && <p className="text-[11px] text-orange-200/40 text-center">{M.rtHintTop}</p>}
          </div>

          {/* BOTTOM pane — YOU, normal orientation */}
          <div className="p-4 flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-2">
              <select value={langA} onChange={e => setLangA(e.target.value)} className="bg-black/50 border border-orange-500/40 rounded-full px-2 py-1 text-xs text-white font-medium max-w-[60vw]" style={{ color: "#fff", backgroundColor: "rgba(0,0,0,0.6)" }} data-testid="rt-lang-a">
                {renderLangOptions()}
              </select>
              <button
                onClick={() => rec === 'a' ? stopRec() : startRec('a')}
                disabled={busy || (rec && rec !== 'a')}
                className={`p-2 rounded-full flex-shrink-0 ${rec === 'a' ? 'bg-red-500 animate-pulse' : 'bg-gradient-to-r from-orange-500 to-pink-500'} disabled:opacity-40`}
                data-testid="rt-mic-a"
              >
                {rec === 'a' ? <Square className="w-3.5 h-3.5" fill="white" /> : <Mic className="w-3.5 h-3.5" />}
              </button>
              {busy === 'a' && <Loader2 className="w-4 h-4 animate-spin text-orange-300" />}
            </div>
            {(textA || transB) && (
              <div className="space-y-1.5">
                {textA && <p className="text-[13px] font-semibold text-orange-100">{textA}</p>}
                {transB && (
                  <p className="text-xs text-orange-200/70 italic flex items-start gap-1">
                    <Volume2 className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{transB}</span>
                  </p>
                )}
              </div>
            )}
            {!textA && !transB && <p className="text-[11px] text-orange-200/40 text-center">{M.rtHintBottom}</p>}
          </div>
          </div>
        </div>
      )}
      {open && (
        <button onClick={() => { setOpen(false); setTextA(''); setTextB(''); setTransA(''); setTransB(''); }} className="mt-2 w-full py-1.5 text-[11px] text-orange-200/60 hover:text-orange-200">
          {M.rtClose}
        </button>
      )}
    </div>
  );
}
